/**
 * A function to produce a single merged image
 * Merges the existing whiteboard drawing with the main illustration
 * and returns a Blob (image/png).
 *
 * @param {Object} canvasRef - The ref to your Fabric.js whiteboard (canvasRef.current is a fabric.Canvas).
 * @returns {Promise<Blob>} - Resolves with a merged PNG Blob, or rejects on error.
 */
export function mergeCanvasWithIllustration(canvasRef) {
    return new Promise((resolve, reject) => {
        // 1) Find the main illustration <img> in the DOM
        const mainIllEl = document.getElementById('main-illustration-img');
        if (!mainIllEl) {
            return reject(new Error('No main illustration element found in the DOM.'));
        }

        // The <img> has base64 data in its "src" attribute
        // We can use naturalWidth / naturalHeight to get the image's true size
        const naturalWidth = mainIllEl.naturalWidth;
        const naturalHeight = mainIllEl.naturalHeight;
        if (!naturalWidth || !naturalHeight) {
            return reject(new Error('Main illustration image missing dimensions.'));
        }

        // 2) Ensure we have a valid Fabric canvas
        if (!canvasRef?.current) {
            return reject(new Error('No Fabric.js whiteboard found.'));
        }

        // 3) Convert the whiteboard (fabric.Canvas) to a PNG data URL
        //    (We want everything the user drew on top of a transparent background.)
        const whiteboardPngUrl = canvasRef.current.toDataURL({
            format: 'png',
            multiplier: 1, // or 2 if you want higher resolution
        });

        // 4) Create an offscreen canvas with the same size as the main illustration
        const offCanvas = document.createElement('canvas');
        offCanvas.width = naturalWidth;
        offCanvas.height = naturalHeight;
        const ctx = offCanvas.getContext('2d');

        // 5) We'll load the main illustration's actual image data
        const mainImage = new Image();
        mainImage.crossOrigin = 'Anonymous'; // if needed, just in case
        mainImage.src = mainIllEl.src; // This is the base64 data from <img>

        mainImage.onload = () => {
            // Draw the main illustration onto the offscreen canvas
            ctx.drawImage(mainImage, 0, 0, naturalWidth, naturalHeight);

            // Next, load the whiteboard as an <img> so we can draw it on top
            const wbImg = new Image();
            wbImg.crossOrigin = 'Anonymous';
            wbImg.src = whiteboardPngUrl;

            wbImg.onload = () => {
                // Draw the whiteboard PNG on top, aligning to the same size
                ctx.drawImage(wbImg, 0, 0, naturalWidth, naturalHeight);

                // 6) Convert the offscreen canvas to a PNG blob
                offCanvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('Failed to create PNG blob.'));
                        } else {
                            resolve(blob);
                        }
                    },
                    'image/png',
                    // 0.9 /* quality */, leave it for now
                );
            };

            wbImg.onerror = (err) => reject(err);
        };

        mainImage.onerror = (err) => reject(err);
    });
}
